import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  CreditCard, 
  Users, 
  Calendar, 
  Download, 
  Filter, 
  Eye, 
  BarChart3, 
  PieChart, 
  LineChart,
  FileText,
  Calculator,
  Percent,
  Target,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  X,
  Printer,
  Mail,
  RefreshCw
} from "lucide-react";

// Firebase imports
import { collection, getDocs, doc, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';

const FinancialReports = ({ user, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // États pour les données financières
  const [overviewData, setOverviewData] = useState({
    totalRevenue: 0,
    monthlyGrowth: 0,
    totalTransactions: 0,
    averageOrderValue: 0,
    platformCommission: 0,
    netRevenue: 0,
    refunds: 0,
    pendingPayments: 0
  });

  const [revenueData, setRevenueData] = useState([]);
  const [workshopPerformance, setWorkshopPerformance] = useState([]);
  const [chefPerformance, setChefPerformance] = useState([]);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [customerMetrics, setCustomerMetrics] = useState({});
  const [projections, setProjections] = useState([]);
  const [taxData, setTaxData] = useState({});

  // États pour les filtres
  const [filters, setFilters] = useState({
    period: 'month', // week, month, quarter, year
    workshop: 'all',
    chef: 'all',
    paymentMethod: 'all',
    status: 'all'
  });

  // États pour les alertes
  const [showAlert, setShowAlert] = useState(false);
  const [alertData, setAlertData] = useState({
    title: '',
    message: '',
    type: 'success'
  });

  useEffect(() => {
    loadFinancialData();
  }, [dateRange, filters]);

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      console.log('📊 Chargement des données financières...');
      
      // Pour cette démo, nous utiliserons des données simulées réalistes
      loadDemoFinancialData();
      
    } catch (error) {
      console.error('Erreur lors du chargement des données financières:', error);
      showAlertMessage('Erreur', 'Impossible de charger les données financières', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadDemoFinancialData = () => {
    // Données de vue d'ensemble
    const overview = {
      totalRevenue: 45267.50,
      monthlyGrowth: 12.5,
      totalTransactions: 342,
      averageOrderValue: 132.35,
      platformCommission: 6790.13,
      netRevenue: 38477.37,
      refunds: 1250.00,
      pendingPayments: 2100.00
    };
    setOverviewData(overview);

    // Données de revenus par période
    const revenue = [
      { period: 'Jan 2024', revenue: 32500, transactions: 285, growth: 8.2 },
      { period: 'Fév 2024', revenue: 35200, transactions: 298, growth: 8.3 },
      { period: 'Mar 2024', revenue: 38900, transactions: 315, growth: 10.5 },
      { period: 'Avr 2024', revenue: 42150, transactions: 328, growth: 8.4 },
      { period: 'Mai 2024', revenue: 39800, transactions: 309, growth: -5.6 },
      { period: 'Jun 2024', revenue: 45267, transactions: 342, growth: 13.7 }
    ];
    setRevenueData(revenue);

    // Performance des ateliers
    const workshops = [
      { 
        id: 1, 
        name: 'Macarons Parisiens', 
        chef: 'Chef Marie Dubois',
        revenue: 8450.00, 
        bookings: 65, 
        averagePrice: 130, 
        rating: 4.8,
        profitMargin: 68.5,
        category: 'Pâtisserie'
      },
      { 
        id: 2, 
        name: 'Cuisine Méditerranéenne', 
        chef: 'Chef Antoine Moreau',
        revenue: 7200.00, 
        bookings: 48, 
        averagePrice: 150, 
        rating: 4.6,
        profitMargin: 72.3,
        category: 'Cuisine du monde'
      },
      { 
        id: 3, 
        name: 'Sushi Traditionnel', 
        chef: 'Chef Kenji Yamamoto',
        revenue: 6890.00, 
        bookings: 42, 
        averagePrice: 164, 
        rating: 4.9,
        profitMargin: 65.2,
        category: 'Cuisine japonaise'
      },
      { 
        id: 4, 
        name: 'Boulangerie Artisanale', 
        chef: 'Chef Pierre Boulanger',
        revenue: 5650.00, 
        bookings: 58, 
        averagePrice: 97, 
        rating: 4.4,
        profitMargin: 71.8,
        category: 'Boulangerie'
      },
      { 
        id: 5, 
        name: 'Cuisine Italienne', 
        chef: 'Chef Sofia Romano',
        revenue: 4920.00, 
        bookings: 35, 
        averagePrice: 140, 
        rating: 4.7,
        profitMargin: 69.4,
        category: 'Cuisine italienne'
      }
    ];
    setWorkshopPerformance(workshops);

    // Performance des chefs
    const chefs = [
      {
        id: 1,
        name: 'Chef Marie Dubois',
        speciality: 'Pâtisserie française',
        totalRevenue: 12450.00,
        workshops: 3,
        bookings: 89,
        rating: 4.8,
        commission: 1867.50,
        joinDate: '2023-01-15'
      },
      {
        id: 2,
        name: 'Chef Antoine Moreau',
        speciality: 'Cuisine méditerranéenne',
        totalRevenue: 9800.00,
        workshops: 2,
        bookings: 67,
        rating: 4.6,
        commission: 1470.00,
        joinDate: '2023-03-22'
      },
      {
        id: 3,
        name: 'Chef Kenji Yamamoto',
        speciality: 'Cuisine japonaise',
        totalRevenue: 8950.00,
        workshops: 2,
        bookings: 54,
        rating: 4.9,
        commission: 1342.50,
        joinDate: '2023-02-10'
      }
    ];
    setChefPerformance(chefs);

    // Historique des transactions
    const transactions = [
      {
        id: 'TXN-2024-001',
        date: '2024-06-15T14:30:00',
        workshop: 'Macarons Parisiens',
        customer: 'Marie Martin',
        amount: 130.00,
        commission: 19.50,
        paymentMethod: 'Carte bancaire',
        status: 'Confirmé',
        chef: 'Chef Marie Dubois'
      },
      {
        id: 'TXN-2024-002',
        date: '2024-06-14T16:45:00',
        workshop: 'Cuisine Méditerranéenne',
        customer: 'Pierre Durand',
        amount: 150.00,
        commission: 22.50,
        paymentMethod: 'PayPal',
        status: 'Confirmé',
        chef: 'Chef Antoine Moreau'
      },
      {
        id: 'TXN-2024-003',
        date: '2024-06-13T11:20:00',
        workshop: 'Sushi Traditionnel',
        customer: 'Sophie Leclerc',
        amount: 164.00,
        commission: 24.60,
        paymentMethod: 'Carte bancaire',
        status: 'En attente',
        chef: 'Chef Kenji Yamamoto'
      },
      {
        id: 'TXN-2024-004',
        date: '2024-06-12T09:15:00',
        workshop: 'Boulangerie Artisanale',
        customer: 'Julien Bernard',
        amount: 97.00,
        commission: 14.55,
        paymentMethod: 'Virement',
        status: 'Confirmé',
        chef: 'Chef Pierre Boulanger'
      },
      {
        id: 'TXN-2024-005',
        date: '2024-06-11T13:40:00',
        workshop: 'Macarons Parisiens',
        customer: 'Isabelle Moreau',
        amount: 130.00,
        commission: 19.50,
        paymentMethod: 'Carte bancaire',
        status: 'Remboursé',
        chef: 'Chef Marie Dubois'
      }
    ];
    setTransactionHistory(transactions);

    // Métriques clients
    const customers = {
      totalCustomers: 1247,
      newCustomers: 89,
      returningCustomers: 203,
      customerRetentionRate: 68.5,
      averageLifetimeValue: 285.50,
      topSpendingSegment: 'Professionnels de la restauration',
      acquisitionCost: 45.20,
      satisfactionScore: 4.6
    };
    setCustomerMetrics(customers);

    // Projections
    const forecast = [
      { month: 'Jul 2024', projected: 47200, confidence: 85 },
      { month: 'Aoû 2024', projected: 52100, confidence: 78 },
      { month: 'Sep 2024', projected: 48900, confidence: 72 },
      { month: 'Oct 2024', projected: 54200, confidence: 68 },
      { month: 'Nov 2024', projected: 58500, confidence: 65 },
      { month: 'Déc 2024', projected: 62800, confidence: 62 }
    ];
    setProjections(forecast);

    // Données fiscales
    const tax = {
      totalTaxableRevenue: 45267.50,
      vatCollected: 9053.50,
      vatRate: 20,
      taxPeriod: 'T2 2024',
      professionalExpenses: 8950.00,
      netTaxableIncome: 36317.50,
      estimatedTax: 7263.50,
      lastDeclaration: '2024-04-30',
      nextDeadline: '2024-07-31'
    };
    setTaxData(tax);

    console.log('✅ Données financières de démo chargées');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Confirmé': 'default',
      'En attente': 'secondary',
      'Remboursé': 'destructive',
      'Annulé': 'outline'
    };
    
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const exportReport = async (format, reportType) => {
    try {
      showAlertMessage('Export', `Génération du rapport ${reportType} en cours (${format.toUpperCase()})...`, 'success');
      
      // Simuler l'export
      setTimeout(() => {
        showAlertMessage('Succès', `Rapport ${reportType} exporté avec succès !`, 'success');
      }, 2000);
      
    } catch (error) {
      showAlertMessage('Erreur', 'Erreur lors de l\'export du rapport', 'error');
    }
  };

  const showAlertMessage = (title, message, type) => {
    setAlertData({ title, message, type });
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 4000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p>Chargement des rapports financiers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Rapports Financiers</h1>
            <p className="text-gray-600">Analyse complète des performances financières de CETROBON</p>
          </div>
          <div className="flex space-x-3">
            <div className="flex space-x-2">
              <Input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({...prev, startDate: e.target.value}))}
                className="w-40"
              />
              <Input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({...prev, endDate: e.target.value}))}
                className="w-40"
              />
            </div>
            <Button onClick={() => loadFinancialData()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4 mr-2" />
              Fermer
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-8">
            <TabsTrigger value="overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="revenue">
              <TrendingUp className="w-4 h-4 mr-2" />
              Revenus
            </TabsTrigger>
            <TabsTrigger value="workshops">
              <Target className="w-4 h-4 mr-2" />
              Ateliers
            </TabsTrigger>
            <TabsTrigger value="chefs">
              <Users className="w-4 h-4 mr-2" />
              Chefs
            </TabsTrigger>
            <TabsTrigger value="transactions">
              <CreditCard className="w-4 h-4 mr-2" />
              Transactions
            </TabsTrigger>
            <TabsTrigger value="customers">
              <Users className="w-4 h-4 mr-2" />
              Clients
            </TabsTrigger>
            <TabsTrigger value="projections">
              <LineChart className="w-4 h-4 mr-2" />
              Prévisions 
            </TabsTrigger>
            <TabsTrigger value="taxes">
              <Calculator className="w-4 h-4 mr-2" />
              Fiscalité
            </TabsTrigger>
          </TabsList>

          {/* Onglet Vue d'ensemble */}
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Chiffre d'affaires total</p>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(overviewData.totalRevenue)}</p>
                      <div className="flex items-center mt-1">
                        <ArrowUpRight className="w-4 h-4 text-green-500 mr-1" />
                        <span className="text-sm text-green-500">+{overviewData.monthlyGrowth}%</span>
                      </div>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Transactions</p>
                      <p className="text-2xl font-bold text-blue-600">{overviewData.totalTransactions}</p>
                      <p className="text-sm text-gray-500">Panier moyen: {formatCurrency(overviewData.averageOrderValue)}</p>
                    </div>
                    <CreditCard className="w-8 h-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Commission plateforme</p>
                      <p className="text-2xl font-bold text-orange-600">{formatCurrency(overviewData.platformCommission)}</p>
                      <p className="text-sm text-gray-500">15% du CA</p>
                    </div>
                    <Percent className="w-8 h-8 text-orange-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Revenus nets</p>
                      <p className="text-2xl font-bold text-purple-600">{formatCurrency(overviewData.netRevenue)}</p>
                      <p className="text-sm text-gray-500">Après commissions</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Graphique des revenus mensuels */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle>Évolution des revenus (6 derniers mois)</CardTitle>
                  <CardDescription>Chiffre d'affaires mensuel et croissance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-end justify-between space-x-2">
                    {revenueData.map((data, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <div 
                          className="bg-orange-500 rounded-t"
                          style={{ 
                            height: `${(data.revenue / Math.max(...revenueData.map(d => d.revenue))) * 200}px`,
                            width: '40px'
                          }}
                        />
                        <div className="mt-2 text-center">
                          <p className="text-xs font-medium">{data.period}</p>
                          <p className="text-xs text-gray-500">{formatCurrency(data.revenue)}</p>
                          <p className={`text-xs ${data.growth > 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {data.growth > 0 ? '+' : ''}{data.growth}%
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Indicateurs clés</CardTitle>
                  <CardDescription>Métriques importantes ce mois</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Remboursements</span>
                    <div className="text-right">
                      <span className="font-medium text-red-600">{formatCurrency(overviewData.refunds)}</span>
                      <p className="text-xs text-gray-500">2.8% du CA</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Paiements en attente</span>
                    <div className="text-right">
                      <span className="font-medium text-yellow-600">{formatCurrency(overviewData.pendingPayments)}</span>
                      <p className="text-xs text-gray-500">4.6% du CA</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Taux de conversion</span>
                    <div className="text-right">
                      <span className="font-medium text-green-600">3.2%</span>
                      <p className="text-xs text-gray-500">+0.3 pts vs mois dernier</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Satisfaction client</span>
                    <div className="text-right">
                      <span className="font-medium text-blue-600">4.7/5</span>
                      <p className="text-xs text-gray-500">+0.1 vs mois dernier</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Actions rapides */}
            <Card>
              <CardHeader>
                <CardTitle>Actions rapides</CardTitle>
                <CardDescription>Générer des rapports et exports</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button onClick={() => exportReport('pdf', 'Vue d\'ensemble')}>
                    <FileText className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                  <Button variant="outline" onClick={() => exportReport('excel', 'Données détaillées')}>
                    <Download className="w-4 h-4 mr-2" />
                    Export Excel
                  </Button>
                  <Button variant="outline" onClick={() => showAlertMessage('Email', 'Rapport envoyé par email !', 'success')}>
                    <Mail className="w-4 h-4 mr-2" />
                    Envoyer par email
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Onglet Revenus */}
          <TabsContent value="revenue" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Évolution détaillée des revenus</CardTitle>
                  <CardDescription>Analyse mensuelle avec tendances</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Période</th>
                          <th className="text-right py-2">Revenus</th>
                          <th className="text-right py-2">Transactions</th>
                          <th className="text-right py-2">Croissance</th>
                          <th className="text-right py-2">Panier moyen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {revenueData.map((data, index) => (
                          <tr key={index} className="border-b">
                            <td className="py-3 font-medium">{data.period}</td>
                            <td className="text-right py-3">{formatCurrency(data.revenue)}</td>
                            <td className="text-right py-3">{data.transactions}</td>
                            <td className={`text-right py-3 ${data.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {data.growth > 0 ? '+' : ''}{data.growth}%
                            </td>
                            <td className="text-right py-3">{formatCurrency(data.revenue / data.transactions)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Répartition par méthode de paiement</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Carte bancaire</span>
                      <div className="text-right">
                        <span className="font-medium">68%</span>
                        <div className="w-20 h-2 bg-gray-200 rounded-full mt-1">
                          <div className="w-[68%] h-2 bg-blue-500 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">PayPal</span>
                      <div className="text-right">
                        <span className="font-medium">22%</span>
                        <div className="w-20 h-2 bg-gray-200 rounded-full mt-1">
                          <div className="w-[22%] h-2 bg-yellow-500 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Virement</span>
                      <div className="text-right">
                        <span className="font-medium">10%</span>
                        <div className="w-20 h-2 bg-gray-200 rounded-full mt-1">
                          <div className="w-[10%] h-2 bg-green-500 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-end space-x-2">
              <Button onClick={() => exportReport('pdf', 'Revenus')}>
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
              <Button variant="outline" onClick={() => exportReport('excel', 'Revenus')}>
                <FileText className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </TabsContent>

          {/* Onglet Performance des Ateliers */}
          <TabsContent value="workshops" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Performance des ateliers</CardTitle>
                <CardDescription>Analyse détaillée par atelier et catégorie</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3">Atelier</th>
                        <th className="text-left py-3">Chef</th>
                        <th className="text-left py-3">Catégorie</th>
                        <th className="text-right py-3">Revenus</th>
                        <th className="text-right py-3">Réservations</th>
                        <th className="text-right py-3">Prix moyen</th>
                        <th className="text-right py-3">Note</th>
                        <th className="text-right py-3">Marge</th>
                        <th className="text-right py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workshopPerformance.map((workshop) => (
                        <tr key={workshop.id} className="border-b hover:bg-gray-50">
                          <td className="py-4 font-medium">{workshop.name}</td>
                          <td className="py-4 text-gray-600">{workshop.chef}</td>
                          <td className="py-4">
                            <Badge variant="outline">{workshop.category}</Badge>
                          </td>
                          <td className="text-right py-4 font-medium text-green-600">
                            {formatCurrency(workshop.revenue)}
                          </td>
                          <td className="text-right py-4">{workshop.bookings}</td>
                          <td className="text-right py-4">{formatCurrency(workshop.averagePrice)}</td>
                          <td className="text-right py-4">
                            <div className="flex items-center justify-end">
                              <span className="mr-1">⭐</span>
                              <span>{workshop.rating}</span>
                            </div>
                          </td>
                          <td className="text-right py-4 text-blue-600 font-medium">
                            {workshop.profitMargin}%
                          </td>
                          <td className="text-right py-4">
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900">Top Performer</h4>
                    <p className="text-blue-700">Macarons Parisiens</p>
                    <p className="text-sm text-blue-600">{formatCurrency(8450)} de revenus</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-green-900">Meilleure marge</h4>
                    <p className="text-green-700">Cuisine Méditerranéenne</p>
                    <p className="text-sm text-green-600">72.3% de marge</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="font-medium text-yellow-900">Plus populaire</h4>
                    <p className="text-yellow-700">Macarons Parisiens</p>
                    <p className="text-sm text-yellow-600">65 réservations</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Onglet Performance des Chefs */}
          <TabsContent value="chefs" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Performance des chefs partenaires</CardTitle>
                <CardDescription>Analyse des revenus et performance par chef</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3">Chef</th>
                        <th className="text-left py-3">Spécialité</th>
                        <th className="text-right py-3">Revenus totaux</th>
                        <th className="text-right py-3">Ateliers</th>
                        <th className="text-right py-3">Réservations</th>
                        <th className="text-right py-3">Note moyenne</th>
                        <th className="text-right py-3">Commission</th>
                        <th className="text-right py-3">Membre depuis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chefPerformance.map((chef) => (
                        <tr key={chef.id} className="border-b hover:bg-gray-50">
                          <td className="py-4 font-medium">{chef.name}</td>
                          <td className="py-4 text-gray-600">{chef.speciality}</td>
                          <td className="text-right py-4 font-medium text-green-600">
                            {formatCurrency(chef.totalRevenue)}
                          </td>
                          <td className="text-right py-4">{chef.workshops}</td>
                          <td className="text-right py-4">{chef.bookings}</td>
                          <td className="text-right py-4">
                            <div className="flex items-center justify-end">
                              <span className="mr-1">⭐</span>
                              <span>{chef.rating}</span>
                            </div>
                          </td>
                          <td className="text-right py-4 font-medium text-orange-600">
                            {formatCurrency(chef.commission)}
                          </td>
                          <td className="text-right py-4 text-gray-500">
                            {new Date(chef.joinDate).toLocaleDateString('fr-FR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Onglet Transactions */}
          <TabsContent value="transactions" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Historique des transactions</CardTitle>
                <CardDescription>Suivi détaillé de toutes les transactions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4 flex space-x-4">
                  <select 
                    className="px-3 py-2 border border-gray-300 rounded-md"
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({...prev, status: e.target.value}))}
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="Confirmé">Confirmé</option>
                    <option value="En attente">En attente</option>
                    <option value="Remboursé">Remboursé</option>
                  </select>
                  <select 
                    className="px-3 py-2 border border-gray-300 rounded-md"
                    value={filters.paymentMethod}
                    onChange={(e) => setFilters(prev => ({...prev, paymentMethod: e.target.value}))}
                  >
                    <option value="all">Toutes les méthodes</option>
                    <option value="Carte bancaire">Carte bancaire</option>
                    <option value="PayPal">PayPal</option>
                    <option value="Virement">Virement</option>
                  </select>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3">ID Transaction</th>
                        <th className="text-left py-3">Date</th>
                        <th className="text-left py-3">Atelier</th>
                        <th className="text-left py-3">Client</th>
                        <th className="text-left py-3">Chef</th>
                        <th className="text-right py-3">Montant</th>
                        <th className="text-right py-3">Commission</th>
                        <th className="text-left py-3">Méthode</th>
                        <th className="text-left py-3">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactionHistory.map((transaction) => (
                        <tr key={transaction.id} className="border-b hover:bg-gray-50">
                          <td className="py-4 font-mono text-sm">{transaction.id}</td>
                          <td className="py-4 text-sm">{formatDate(transaction.date)}</td>
                          <td className="py-4">{transaction.workshop}</td>
                          <td className="py-4">{transaction.customer}</td>
                          <td className="py-4 text-gray-600">{transaction.chef}</td>
                          <td className="text-right py-4 font-medium">
                            {formatCurrency(transaction.amount)}
                          </td>
                          <td className="text-right py-4 text-orange-600">
                            {formatCurrency(transaction.commission)}
                          </td>
                          <td className="py-4">
                            <Badge variant="outline">{transaction.paymentMethod}</Badge>
                          </td>
                          <td className="py-4">
                            {getStatusBadge(transaction.status)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Onglet Clients */}
          <TabsContent value="customers" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Clients totaux</p>
                      <p className="text-2xl font-bold text-blue-600">{customerMetrics.totalCustomers}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Nouveaux clients</p>
                      <p className="text-2xl font-bold text-green-600">{customerMetrics.newCustomers}</p>
                      <p className="text-sm text-gray-500">Ce mois</p>
                    </div>
                    <ArrowUpRight className="w-8 h-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Clients fidèles</p>
                      <p className="text-2xl font-bold text-purple-600">{customerMetrics.returningCustomers}</p>
                      <p className="text-sm text-gray-500">{customerMetrics.customerRetentionRate}% rétention</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Valeur vie client</p>
                      <p className="text-2xl font-bold text-orange-600">{formatCurrency(customerMetrics.averageLifetimeValue)}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-orange-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Analyse comportementale</CardTitle>
                  <CardDescription>Métriques d'engagement client</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Coût d'acquisition</span>
                    <span className="font-medium">{formatCurrency(customerMetrics.acquisitionCost)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Score de satisfaction</span>
                    <div className="flex items-center">
                      <span className="font-medium mr-1">{customerMetrics.satisfactionScore}/5</span>
                      <span className="text-yellow-500">⭐</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Segment principal</span>
                    <Badge>{customerMetrics.topSpendingSegment}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Taux de recommandation</span>
                    <span className="font-medium text-green-600">87%</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Segmentation clients</CardTitle>
                  <CardDescription>Répartition par type de client</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Particuliers passionnés</span>
                      <div className="text-right">
                        <span className="font-medium">45%</span>
                        <div className="w-20 h-2 bg-gray-200 rounded-full mt-1">
                          <div className="w-[45%] h-2 bg-blue-500 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Professionnels resto</span>
                      <div className="text-right">
                        <span className="font-medium">28%</span>
                        <div className="w-20 h-2 bg-gray-200 rounded-full mt-1">
                          <div className="w-[28%] h-2 bg-green-500 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Entreprises (team building)</span>
                      <div className="text-right">
                        <span className="font-medium">18%</span>
                        <div className="w-20 h-2 bg-gray-200 rounded-full mt-1">
                          <div className="w-[18%] h-2 bg-purple-500 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Écoles culinaires</span>
                      <div className="text-right">
                        <span className="font-medium">9%</span>
                        <div className="w-20 h-2 bg-gray-200 rounded-full mt-1">
                          <div className="w-[9%] h-2 bg-orange-500 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Onglet Prévisions */}
          <TabsContent value="projections" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Prévisions financières</CardTitle>
                <CardDescription>Projections basées sur les tendances historiques</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <div className="h-64 flex items-end justify-between space-x-2">
                    {projections.map((projection, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <div className="flex flex-col items-center mb-2">
                          <span className="text-xs text-gray-500">{projection.confidence}%</span>
                          <span className="text-xs bg-gray-200 px-1 rounded">confiance</span>
                        </div>
                        <div 
                          className="bg-gradient-to-t from-blue-500 to-blue-300 rounded-t"
                          style={{ 
                            height: `${(projection.projected / Math.max(...projections.map(p => p.projected))) * 180}px`,
                            width: '50px'
                          }}
                        />
                        <div className="mt-2 text-center">
                          <p className="text-xs font-medium">{projection.month}</p>
                          <p className="text-xs text-gray-600">{formatCurrency(projection.projected)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-green-900">Croissance prévue</h4>
                    <p className="text-2xl font-bold text-green-700">+23%</p>
                    <p className="text-sm text-green-600">Sur les 6 prochains mois</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900">CA prévisionnel</h4>
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(323600)}</p>
                    <p className="text-sm text-blue-600">Juillet - Décembre 2024</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-medium text-purple-900">Confiance moyenne</h4>
                    <p className="text-2xl font-bold text-purple-700">72%</p>
                    <p className="text-sm text-purple-600">Basé sur historique 18 mois</p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <div className="flex items-start">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-yellow-800">Facteurs d'incertitude</h4>
                      <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                        <li>• Saisonnalité estivale (juin-août) pouvant affecter les réservations</li>
                        <li>• Lancement de nouveaux ateliers prévu en septembre</li>
                        <li>• Campagne marketing Q4 non encore budgétée</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Onglet Fiscalité */}
          <TabsContent value="taxes" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle>Situation fiscale</CardTitle>
                  <CardDescription>Période : {taxData.taxPeriod}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">CA total taxable</span>
                    <span className="font-medium">{formatCurrency(taxData.totalTaxableRevenue)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">TVA collectée ({taxData.vatRate}%)</span>
                    <span className="font-medium text-blue-600">{formatCurrency(taxData.vatCollected)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Charges professionnelles</span>
                    <span className="font-medium text-red-600">-{formatCurrency(taxData.professionalExpenses)}</span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Bénéfice imposable</span>
                      <span className="font-bold text-green-600">{formatCurrency(taxData.netTaxableIncome)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Impôt estimé (20%)</span>
                    <span className="font-medium text-orange-600">{formatCurrency(taxData.estimatedTax)}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Échéances fiscales</CardTitle>
                  <CardDescription>Calendrier des obligations</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium">Déclaration T1</p>
                      <p className="text-sm text-gray-600">Terminée le {new Date(taxData.lastDeclaration).toLocaleDateString('fr-FR')}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-medium">Déclaration T2</p>
                      <p className="text-sm text-gray-600">À faire avant le {new Date(taxData.nextDeadline).toLocaleDateString('fr-FR')}</p>
                      <Badge variant="outline" className="mt-1 text-orange-600">Échéance dans 15 jours</Badge>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium">TVA trimestrielle</p>
                      <p className="text-sm text-gray-600">Prochaine échéance : 20 septembre 2024</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Actions fiscales</CardTitle>
                <CardDescription>Outils et exports pour la comptabilité</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Button onClick={() => exportReport('pdf', 'Déclaration fiscale')}>
                    <FileText className="w-4 h-4 mr-2" />
                    Export déclaration
                  </Button>
                  <Button variant="outline" onClick={() => exportReport('excel', 'Journal comptable')}>
                    <Calculator className="w-4 h-4 mr-2" />
                    Journal comptable
                  </Button>
                  <Button variant="outline" onClick={() => exportReport('pdf', 'Factures TVA')}>
                    <Percent className="w-4 h-4 mr-2" />
                    Rapport TVA
                  </Button>
                  <Button variant="outline" onClick={() => showAlertMessage('Expert-comptable', 'Données transmises à votre expert-comptable !', 'success')}>
                    <Mail className="w-4 h-4 mr-2" />
                    Envoi comptable
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Alert Modal */}
      {showAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              {alertData.type === 'success' ? (
                <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              )}
              <h3 className="text-lg font-semibold">{alertData.title}</h3>
            </div>
            <p className="text-gray-600 mb-4">{alertData.message}</p>
            <div className="flex justify-end">
              <Button onClick={() => setShowAlert(false)}>
                OK
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialReports;